import type React from "react";
import { useState } from "react";
import type { ChooseCampaignOptionsProps } from "./types";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogTitle,
} from "../misc/Dialog";
import AutoVideoPreferences from "../settings/AutoVideoPreferences/AutoVideoPreferences";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../misc/Card";
import { Button } from "../misc/Button";
import { Label } from "../misc/Label";
import { Switch } from "../misc/Switch";
import ReconfirmOverlay from "../misc/ReconfirmOverlay";
import { Popover, PopoverContent, PopoverTrigger } from "../misc/Popover";
import { cn } from "../../utils/classMerger";
import { CalendarIcon, Info, X } from "lucide-react";
import { format } from "date-fns";
import CustomDatePicker from "../misc/CutomDatePicker";
import { toast } from "react-toastify";
import CustomToast from "../misc/CustomToast";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../listings/slideshow/Tooltip";
import { useIsMobile } from "../../hooks/useIsMobile";
import CustomDialog from "../misc/CustomDialog";

const ChooseCampaignOptions: React.FC<ChooseCampaignOptionsProps> = ({
    setCurrentStep,
    autoApprove,
    setAutoApprove,
    autoGenerateSlideshow,
    setAutoGenerateSlideshow,
    listingType,
    date,
    setDate,
    firstImage,
    setFirstImage,
    floorPlan,
    setFloorPlan,
    cta,
    setCta,
    openHouseMonitoring,
    setOpenHouseMonitoring,
    autoVideoReady,
    onAutoVideoConfigured,
    onNext,
}) => {
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [isFloorPlanOverlayOpen, setIsFloorPlanOverlayOpen] = useState(false);

    const isMobile = useIsMobile(900);

    const [openInfoDialog, setOpenInfoDialog] = useState(false);
    const [autoVideoSetupOpen, setAutoVideoSetupOpen] = useState(false);

    // The dialog owns turning the toggle on, so take the value straight from it:
    // in edit mode the readiness refresh deliberately leaves the campaign's own
    // value alone, so it can't be relied on to flip this on.
    const handleAutoVideoSetupSaved = (enabled: boolean): void => {
        setAutoVideoSetupOpen(false);
        setAutoGenerateSlideshow(enabled);
        onAutoVideoConfigured?.();
    };

    const handleBack = () => {
        setCurrentStep((prevStep) => {
            if (prevStep <= 0) {
                return prevStep;
            }
            return prevStep - 1;
        });
    };

    const nextHandler = () => {
        // Edit mode passes no setDate, so the start-date picker above isn't
        // rendered — the campaign already started and its date can't change.
        // Requiring a date here would strand the user on a step with no way
        // to satisfy it.
        if (setDate && !date) {
            toast.error(
                <CustomToast
                    message="Please select a start date for your campaign."
                    type="error"
                />,

                {
                    autoClose: 3000,
                }
            );
        } else if (onNext) {
            onNext();
        } else {
            setCurrentStep((prevStep) => {
                return prevStep + 1;
            });
        }
    };

    const handleAutoApproveChange = (checked: boolean) => {
        if (!checked) {
            // User is turning off auto approval, show confirmation
            setIsOverlayOpen(true);
        } else {
            // User is turning on auto approval
            setAutoApprove(true);
        }
    };

    const handleFloorPlanChange = (checked: boolean) => {
        if (checked && !firstImage) {
            // User is turning on floor plan while front property image is off
            setIsFloorPlanOverlayOpen(true);
        } else {
            // Just update the floor plan state
            setFloorPlan(checked);
        }
    };

    const confirmDisableAutoApprove = () => {
        setAutoApprove(false);
        setIsOverlayOpen(false);
    };

    const cancelDisableAutoApprove = () => {
        // Keep auto approval on
        setIsOverlayOpen(false);
    };

    const confirmEnableFrontProperty = () => {
        setFirstImage(true);
        setFloorPlan(true);
        setIsFloorPlanOverlayOpen(false);
    };

    const cancelEnableFrontProperty = () => {
        // Just enable floor plan without changing front property setting
        setFloorPlan(true);
        setIsFloorPlanOverlayOpen(false);
    };

    const handleOpenHouseMonitoringChange = (checked: boolean) => {
        setOpenHouseMonitoring(checked);
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>Campaign Settings</CardTitle>
                <CardDescription>
                    Select your campaign options by choosing a start date and
                    enabling auto-approval for posts.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-0 mt-0">
                {setDate && (
                    <div>
                        <Label className="text-primary font-semibold">
                            Start Date
                        </Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    className={cn(
                                        "w-full justify-start text-left font-normal text-white",
                                        !date && "text-muted-foreground"
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4 " />
                                    {date ? format(date, "PPP") : "Pick a date"}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                className="w-auto p-0"
                                align="start"
                            >
                                <CustomDatePicker
                                    date={date}
                                    setDate={setDate}
                                    restriction="fortnight"
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                )}

                {listingType === "MLSListing" && (
                    <>
                        <div className="flex items-center justify-between space-x-2">
                            <Label
                                htmlFor="open-house-monitoring"
                                className="text-primary flex flex-col space-y-1 font-semibold"
                            >
                                <span className="flex items-center space-x-[2px]">
                                    <span>MLS Open House Sync</span>
                                    <div
                                        className={`flex ${
                                            isMobile
                                                ? "flex-col justify-center mt-2"
                                                : "flex-row items-center"
                                        } justify-start`}
                                    >
                                        <TooltipProvider>
                                            <Tooltip
                                                {...(openInfoDialog
                                                    ? {
                                                          open: false /* force it closed when dialog is active */,
                                                      }
                                                    : undefined)}
                                            >
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6"
                                                        onClick={() =>
                                                            setOpenInfoDialog(
                                                                true
                                                            )
                                                        }
                                                    >
                                                        <Info className="h-4 w-4" />
                                                        <span className="sr-only">
                                                            MLS Open House Sync
                                                            Info
                                                        </span>
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p className="text-xs text-white">
                                                        Click to learn more
                                                        about MLS Open House
                                                        Sync.
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                </span>
                                <CardDescription className="font-medium">
                                    Marabot automatically syncs open houses from
                                    the MLS and updates your scheduled posts to
                                    highlight upcoming events. Click the info
                                    bubble to learn more.
                                </CardDescription>
                            </Label>
                            <Switch
                                id="open-house-monitoring"
                                checked={openHouseMonitoring}
                                onCheckedChange={
                                    handleOpenHouseMonitoringChange
                                }
                            />
                        </div>
                    </>
                )}

                <div className="flex items-center justify-between space-x-2">
                    <Label
                        htmlFor="auto-approve"
                        className="text-primary flex flex-col space-y-1 font-semibold"
                    >
                        <span>Auto-Approve Posts</span>
                        <span className="text-black font-normal text-sm text-muted-foreground">
                            {autoApprove
                                ? "All social media posts will be automatically approved on creation."
                                : "You will need to manually approve all your posts."}
                        </span>
                        <CardDescription className="font-medium">
                            Auto-Approve is on by default to keep your posts
                            flowing smoothly—no extra steps required! We
                            recommend leaving it on for efficient, uninterrupted
                            scheduling. Switching to manual means you’ll need to
                            approve each post daily before it can go live.
                        </CardDescription>
                    </Label>
                    <Switch
                        id="auto-approve"
                        checked={autoApprove}
                        onCheckedChange={handleAutoApproveChange}
                    />
                </div>

                {listingType && listingType !== "Promotion" && (
                    <>
                        {/* MB-1314 (§O5): auto-video is MLS-only — the Rentcast
                        path is not yet validated, so the opt-in is not offered
                        for Rentcast listings. */}
                        {listingType === "MLSListing" && (
                            <div className="flex items-center justify-between space-x-2">
                                <Label
                                    htmlFor="auto-generate-slideshow"
                                    className="text-primary flex flex-col space-y-1 font-semibold"
                                >
                                    <span>
                                        Automatically Generate Listing Video
                                    </span>
                                    <span className="text-black font-normal text-sm text-muted-foreground">
                                        Marabot will create a branded slideshow
                                        video for this listing at launch and each
                                        renewal.
                                    </span>
                                    {!autoVideoReady && (
                                        <CardDescription className="font-medium text-primary">
                                            Add your brand logo, brand color, and
                                            corporate headshot to enable
                                            auto-video.
                                        </CardDescription>
                                    )}
                                </Label>
                                <Switch
                                    id="auto-generate-slideshow"
                                    checked={autoGenerateSlideshow}
                                    onCheckedChange={(checked) => {
                                        // Turning on always opens the dialog so the
                                        // user reviews their video settings and
                                        // enables it from there — even when setup is
                                        // already complete. Toggling off is direct.
                                        if (checked) {
                                            setAutoVideoSetupOpen(true);
                                            return;
                                        }
                                        setAutoGenerateSlideshow(false);
                                    }}
                                />
                            </div>
                        )}

                        <div className="flex items-center justify-between space-x-2">
                            <Label
                                htmlFor="front-property"
                                className="text-primary flex flex-col space-y-1 font-semibold"
                            >
                                <span>
                                    Display Front of Property in all Posts
                                </span>
                                <CardDescription className="font-medium">
                                    This option ensures that a front exterior
                                    photo of the property appears as the first
                                    image in every post. It applies to the
                                    following post types: General, Feature, and
                                    Open House, and will be used on Facebook,
                                    LinkedIn, X (formerly Twitter), and Google
                                    My Business. Due to platform constraints, it
                                    will not be applied to Instagram or
                                    Pinterest.
                                </CardDescription>
                            </Label>
                            <Switch
                                id="front-property"
                                checked={firstImage}
                                onCheckedChange={() => {
                                    setFirstImage((prev) => !prev);
                                }}
                            />
                        </div>

                        <div className="flex items-center justify-between space-x-2">
                            <Label
                                htmlFor="floor-plan"
                                className="text-primary flex flex-col space-y-1 font-semibold"
                            >
                                <span>Dedicated Floor Plan Posts</span>
                                <CardDescription className="font-medium">
                                    If floor plans are turned off, dedicated
                                    floor plan posts will not be created, but
                                    floor plan images may still appear in
                                    general posts.
                                </CardDescription>
                            </Label>
                            <Switch
                                id="floor-plan"
                                checked={floorPlan}
                                onCheckedChange={handleFloorPlanChange}
                            />
                        </div>

                        <div className="flex items-center justify-between space-x-2">
                            <Label
                                htmlFor="cta-placement"
                                className="text-primary flex flex-col space-y-1 font-semibold"
                            >
                                <span>Call to Action Placement</span>
                                <CardDescription className="font-medium">
                                    Choose where you want the call to action to
                                    appear in your posts. Placing it at the top
                                    makes it immediately visible, while placing
                                    it at the bottom follows the content
                                    naturally.
                                </CardDescription>
                            </Label>
                            <div className="flex space-x-2">
                                <Button
                                    variant={cta === "top" ? "green" : "outv3"}
                                    className="w-[80px]"
                                    size="sm"
                                    onClick={() => setCta("top")}
                                >
                                    Top
                                </Button>
                                <Button
                                    className="w-[80px]"
                                    variant={
                                        cta === "bottom" ? "green" : "outv3"
                                    }
                                    size="sm"
                                    onClick={() => setCta("bottom")}
                                >
                                    Bottom
                                </Button>
                            </div>
                        </div>
                    </>
                )}

                <div className="bg-gray-100 p-3 rounded-md">
                    <p className="text-sm text-gray-600">
                        <strong className="text-primary">Note:</strong> Posts
                        will be generated for an initial two-week period. After
                        that, the system will auto-renew each week, generating
                        posts for the upcoming week. Any additional media that
                        is added to the campaign will be included in the next
                        generation cycle. The campaign will continue
                        automatically until you pause{" "}
                        {listingType && listingType === "Promotion"
                            ? "or delete the promotion"
                            : "it or the property sells"}
                    </p>
                </div>

                {/* Reconfirm Overlay for Auto Approve Posts */}
                <ReconfirmOverlay
                    isOpen={isOverlayOpen}
                    onClose={cancelDisableAutoApprove}
                    onConfirm={confirmDisableAutoApprove}
                    title="Confirm Action"
                    message="Are you sure you want to turn off auto post approval?"
                    messageTwo={`Disabling this feature means you'll need to manually 
            approve each generated post. Without approved content, your campaign 
            will pause. We strongly recommend keeping automatic approval turned 
            on to ensure uninterrupted social media posting.`}
                    confirmText="Yes, Turn Off"
                />

                {/* Reconfirm Overlay for Floor Plan */}
                <ReconfirmOverlay
                    isOpen={isFloorPlanOverlayOpen}
                    onClose={cancelEnableFrontProperty}
                    onConfirm={confirmEnableFrontProperty}
                    title="Suggested Setting"
                    message="It's recommended to add a front of house image as the first image when using Floor Plan posts."
                    messageTwo="Would you like to turn on 'Display Front of Property in all Posts'?"
                    confirmText="Yes"
                />

                {/* Navigation Buttons */}
                <div className="flex justify-between">
                    <Button variant="secondary" onClick={handleBack}>
                        Back
                    </Button>
                    <Button onClick={nextHandler}>Next</Button>
                </div>

                <CustomDialog
                    open={openInfoDialog}
                    onClose={() => setOpenInfoDialog(false)}
                    className="max-w-4xl w-full"
                >
                    <div className="max-w-4xl min-w-4xl bg-white">
                        <h2 className="text-xl font-bold mb-4 text-primary text-center">
                            MLS Open House Sync
                        </h2>
                        <button
                            className="absolute top-4 right-4 text-gray-500 hover:text-red-500"
                            onClick={() => setOpenInfoDialog(false)}
                        >
                            <X className="h-6 w-6" />
                        </button>
                        <div className="space-y-4">
                            <p className="text-primary font-semibold text-lg">
                                How open houses are selected:
                            </p>
                            <p className="text-gray-700 text-sm">
                                Marabot looks for open houses scheduled within
                                10 days of your post date. If no open houses are
                                found in that timeframe, your post won’t include
                                any open house details.
                            </p>
                            <p className="text-gray-700 text-sm">
                                Once an open house is within range, Marabot will
                                automatically include up to 4 upcoming open
                                houses—regardless of their specific dates. To
                                ensure full weekend coverage, if the last open
                                house falls on a Saturday and there's also one
                                scheduled for Sunday, both will be included,
                                allowing for a maximum of 5.
                            </p>
                            <p className="text-gray-700 text-sm">
                                When open house details are added, Marabot will
                                also update the call to action and move it to
                                the top of the post—regardless of the call to
                                action location you originally selected.
                            </p>
                        </div>
                    </div>
                </CustomDialog>

                <Dialog
                    open={autoVideoSetupOpen}
                    onOpenChange={setAutoVideoSetupOpen}
                >
                    <DialogContent className="max-w-4xl p-0 overflow-y-hidden flex flex-col bg-white">
                        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                            <DialogTitle className="text-primary text-xl font-bold">
                                Set up Listing Video Settings
                            </DialogTitle>
                            <DialogClose
                                aria-label="Close"
                                className="text-gray-500 hover:text-red-500"
                            >
                                <X className="h-6 w-6" />
                            </DialogClose>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto p-6">
                            <AutoVideoPreferences
                                embedded
                                autoGenerateValue={autoGenerateSlideshow}
                                onSaved={handleAutoVideoSetupSaved}
                            />
                        </div>
                    </DialogContent>
                </Dialog>
            </CardContent>
        </Card>
    );
};

export default ChooseCampaignOptions;
